import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import type { PaginationResult } from '@/lib/hooks/usePagination';

interface PaginationProps<T> extends Pick<
  PaginationResult<T>,
  'page' | 'pageCount' | 'total' | 'from' | 'to' | 'setPage' | 'next' | 'prev'
> {
  /** Optional noun for "Showing 1–12 of 47 items". */
  noun?: string;
  className?: string;
}

/**
 * Compact pagination footer with prev/next + page indicator and a
 * "Showing N–M of T" label. Hides itself when there is a single page.
 */
export function Pagination<T>({
  page,
  pageCount,
  total,
  from,
  to,
  setPage,
  next,
  prev,
  noun,
  className,
}: PaginationProps<T>) {
  const t = useT();
  if (pageCount <= 1) return null;
  const nounLabel = noun ?? t('pagination.items');
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-[var(--color-border-subtle)] pt-3 text-xs text-[var(--color-fg-muted)]',
        className,
      )}
    >
      <span>
        {t('pagination.showing')}{' '}
        <span className="font-bold tabular-nums text-[var(--color-fg-primary)]">
          {from}–{to}
        </span>{' '}
        {t('pagination.of')}{' '}
        <span className="font-bold tabular-nums text-[var(--color-fg-primary)]">
          {total}
        </span>{' '}
        {nounLabel}
      </span>

      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={prev}
          disabled={page <= 1}
          aria-label={t('pagination.previousPage')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border-default)] hover:border-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <PageBadges page={page} pageCount={pageCount} setPage={setPage} />
        <button
          type="button"
          onClick={next}
          disabled={page >= pageCount}
          aria-label={t('pagination.nextPage')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-[var(--color-border-default)] hover:border-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Page-number badges with ellipsis ("1 … 4 5 6 … 12"). Always shows current
 * page and its two neighbours, plus the first and last page so users can
 * jump to the ends in one click.
 */
function PageBadges({
  page,
  pageCount,
  setPage,
}: {
  page: number;
  pageCount: number;
  setPage: (p: number) => void;
}) {
  const visible = new Set<number>([1, pageCount, page, page - 1, page + 1]);
  const pages = [...visible]
    .filter((p) => p >= 1 && p <= pageCount)
    .sort((a, b) => a - b);

  const out: Array<number | 'ellipsis'> = [];
  for (let i = 0; i < pages.length; i++) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) out.push('ellipsis');
    out.push(pages[i]);
  }

  return (
    <>
      {out.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e-${i}`} className="px-1 text-[var(--color-fg-subtle)]">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => setPage(p)}
            aria-current={p === page ? 'page' : undefined}
            className={cn(
              'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-md px-2 text-xs font-bold tabular-nums transition-colors',
              p === page
                ? 'bg-[var(--color-accent)] text-[#0A0A0A]'
                : 'border border-[var(--color-border-default)] hover:border-[var(--color-accent)]',
            )}
          >
            {p}
          </button>
        ),
      )}
    </>
  );
}
